import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Dices, Plus, Trash2, Skull, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { FOE_TEMPLATES } from "@/lib/bestiary";
import { EQUIPMENT } from "@/lib/equipment";
import { saveLocalFoe, deleteLocalFoe, loadLocalFoes } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ARMOR_OPTIONS = [
  { v: 0, label: "0 · none" },
  { v: 2, label: "d2 · light" },
  { v: 4, label: "d4 · medium" },
  { v: 6, label: "d6 · heavy" },
  { v: 8, label: "d8 · military" },
];
const WEAPON_TYPES = ["physical", "nano", "cyber"];

const empty = () => ({
  id: null,
  name: "",
  desc: "",
  hp: "d8+2",
  armor: 0,
  morale: 8,
  stats: { prs: "d2", str: "d3", agi: "d3", tou: "d3", kno: "d2" },
  weapons: [{ name: "Fist", damage: "d4", type: "physical" }],
  specials: [""],
});

// Convert a full generated foe (from rollFoe) BACK into a template shape suitable for editing.
function toEditable(gen) {
  return {
    id: gen.id || null,
    name: gen.name || "",
    desc: gen.desc || "",
    hp: gen.hp_die || String(gen.hp || "d8"),
    armor: typeof gen.armor === "number" ? gen.armor : 0,
    morale: gen.morale || 8,
    stats: {
      prs: `d${Math.abs(gen.stats?.presence || 2) + 1}`,
      str: `d${Math.abs(gen.stats?.strength || 3) + 1}`,
      agi: `d${Math.abs(gen.stats?.agility || 3) + 1}`,
      tou: `d${Math.abs(gen.stats?.toughness || 3) + 1}`,
      kno: `d${Math.abs(gen.stats?.knowledge || 2) + 1}`,
    },
    weapons: (gen.weapons || []).map((w) => ({ name: w.name, damage: w.damage, type: w.type || "physical" })),
    specials: (gen.specials && gen.specials.length ? gen.specials : [""]),
  };
}

export default function FoeBuilder({ open, onClose, onSaved, foe = null }) {
  const { user } = useAuth();
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (foe) {
        // Editing an existing foe → hydrate form from template shape
        setForm({
          id: foe.id || null,
          name: foe.name || "",
          desc: foe.desc || "",
          hp: foe.hp || "d8",
          armor: typeof foe.armor === "number" ? foe.armor : 0,
          morale: foe.morale || 8,
          stats: { ...(foe.stats || empty().stats) },
          weapons: (foe.weapons && foe.weapons.length ? foe.weapons : empty().weapons).map((w) => ({
            name: w.name || "", damage: w.damage || "d4", type: w.type || "physical"
          })),
          specials: foe.specials && foe.specials.length ? [...foe.specials] : [""],
        });
      } else {
        setForm(empty());
      }
    }
  }, [open, foe]);

  const patch = (u) => setForm((f) => ({ ...f, ...u }));
  const patchStat = (k, v) => setForm((f) => ({ ...f, stats: { ...f.stats, [k]: v } }));

  // Pull a random built-in template into the form so the GM can tweak it.
  const generateFromRandom = () => {
    const t = FOE_TEMPLATES[Math.floor(Math.random() * FOE_TEMPLATES.length)];
    setForm({
      id: null,
      name: t.name,
      desc: t.desc || "",
      hp: t.hp,
      armor: typeof t.armor === "number" ? t.armor : 0,
      morale: t.morale || 8,
      stats: { ...t.stats },
      weapons: t.weapons.map((w) => ({ name: w.name, damage: w.damage, type: "physical" })),
      specials: [...t.specials],
    });
    toast(`GENERATED · ${t.name} — tweak and save`);
  };

  const addWeapon = () => setForm((f) => ({ ...f, weapons: [...f.weapons, { name: "", damage: "d4", type: "physical" }] }));
  const editWeapon = (i, key, val) => setForm((f) => ({
    ...f,
    weapons: f.weapons.map((w, idx) => idx === i ? { ...w, [key]: val } : w),
  }));
  const removeWeapon = (i) => setForm((f) => ({ ...f, weapons: f.weapons.filter((_, idx) => idx !== i) }));

  const addSpecial = () => setForm((f) => ({ ...f, specials: [...f.specials, ""] }));
  const editSpecial = (i, val) => setForm((f) => ({ ...f, specials: f.specials.map((s, idx) => idx === i ? val : s) }));
  const removeSpecial = (i) => setForm((f) => ({ ...f, specials: f.specials.filter((_, idx) => idx !== i) }));

  const valid = form.name.trim().length > 0 && form.weapons.length > 0 && form.weapons.every((w) => w.name.trim() && w.damage.trim());

  const save = async () => {
    if (!valid) {
      toast.error("NAME + AT LEAST ONE WEAPON REQUIRED");
      return;
    }
    setSaving(true);
    const payload = {
      id: form.id || undefined,
      name: form.name.trim(),
      desc: form.desc.trim(),
      hp: form.hp.trim() || "d8",
      armor: parseInt(form.armor, 10) || 0,
      morale: parseInt(form.morale, 10) || 8,
      stats: form.stats,
      weapons: form.weapons.map((w) => ({ name: w.name.trim(), damage: w.damage.trim(), type: w.type })),
      specials: form.specials.map((s) => s.trim()).filter(Boolean),
    };
    try {
      let saved;
      if (user) {
        const r = await api.post("/foes", payload);
        saved = r.data;
      } else {
        saved = saveLocalFoe({ ...payload, id: form.id });
      }
      toast.success(`FOE SAVED · ${saved.name}`);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error("SAVE FAILED", { description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-magenta w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
            data-testid="foe-builder-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="flex items-center gap-3">
                <Skull className="text-[color:var(--accent-magenta)]" />
                <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tighter">
                  CUSTOM//FOE
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="foe-generate-btn"
                  onClick={generateFromRandom}
                  className="cy-btn cy-btn-cyan text-xs"
                  title="Pull a random book/fan template into the form to tweak"
                >
                  <Dices size={12} className="mr-1" /> GENERATE
                </button>
                <button
                  data-testid="foe-builder-close"
                  onClick={onClose}
                  aria-label="Close"
                  className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {/* Identity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">NAME</div>
                  <input
                    data-testid="foe-name-input"
                    className="cy-input mt-1"
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    placeholder="e.g. WARDEN PRIME"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">DESCRIPTION</div>
                  <input
                    data-testid="foe-desc-input"
                    className="cy-input mt-1"
                    value={form.desc}
                    onChange={(e) => patch({ desc: e.target.value })}
                    placeholder="One line of flavour."
                  />
                </div>
              </div>

              {/* Vitals */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">HP</div>
                  <input
                    data-testid="foe-hp-input"
                    className="cy-input mt-1"
                    value={form.hp}
                    onChange={(e) => patch({ hp: e.target.value })}
                    placeholder="d8+2 or 12"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">ARMOR</div>
                  <select
                    data-testid="foe-armor-select"
                    className="cy-input mt-1"
                    value={form.armor}
                    onChange={(e) => patch({ armor: parseInt(e.target.value, 10) })}
                  >
                    {ARMOR_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">MORALE</div>
                  <input
                    data-testid="foe-morale-input"
                    type="number" min={1} max={12}
                    className="cy-input mt-1"
                    value={form.morale}
                    onChange={(e) => patch({ morale: e.target.value })}
                  />
                </div>
              </div>

              {/* Stat dice */}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">STAT DICE (rolled once at spawn)</div>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    ["prs", "PRS"], ["str", "STR"], ["agi", "AGI"], ["tou", "TOU"], ["kno", "KNO"],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <div className="text-[9px] font-mono text-[color:var(--text-muted)] text-center">{label}</div>
                      <input
                        data-testid={`foe-stat-${k}`}
                        className="cy-input mt-1 text-center"
                        value={form.stats[k]}
                        onChange={(e) => patchStat(k, e.target.value)}
                        placeholder="d3"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Weapons */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">WEAPONS</div>
                  <button data-testid="foe-add-weapon-btn" onClick={addWeapon} className="cy-btn cy-btn-cyan text-[10px] min-h-0 px-2 py-1">
                    <Plus size={10} className="mr-1" /> ADD
                  </button>
                </div>
                <div className="space-y-1">
                  {form.weapons.map((w, i) => (
                    <div key={i} className="grid grid-cols-[1fr_100px_110px_auto] gap-1 items-center" data-testid={`foe-weapon-row-${i}`}>
                      <div className="grid grid-cols-[auto_1fr] gap-1 items-center">
                        <select
                          data-testid={`foe-weapon-preset-${i}`}
                          value=""
                          onChange={(e) => {
                            if (!e.target.value) return;
                            const preset = EQUIPMENT.find((it) => it.id === e.target.value);
                            if (!preset) return;
                            editWeapon(i, "name", preset.name);
                            editWeapon(i, "damage", preset.damage);
                          }}
                          className="cy-input w-[38px] text-center"
                          title="Pick from armoury"
                        >
                          <option value="">◈</option>
                          {EQUIPMENT.filter((it) => it.category === "Weapons").map((it) => (
                            <option key={it.id} value={it.id}>{it.name} ({it.damage}){it.illegal ? " ⚠" : ""}</option>
                          ))}
                        </select>
                        <input className="cy-input" placeholder="Name" value={w.name} onChange={(e) => editWeapon(i, "name", e.target.value)} />
                      </div>
                      <input className="cy-input" placeholder="d6 / d10a" value={w.damage} onChange={(e) => editWeapon(i, "damage", e.target.value)} />
                      <select className="cy-input" value={w.type} onChange={(e) => editWeapon(i, "type", e.target.value)}>
                        {WEAPON_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button
                        onClick={() => removeWeapon(i)}
                        disabled={form.weapons.length === 1}
                        className="p-1 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)] disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Remove weapon"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                  ◈ Pick from armoury or type your own. Damage supports `d6`, `d10a` (autofire), `2d6`, etc.
                </div>
              </div>

              {/* Specials */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">SPECIALS</div>
                  <button data-testid="foe-add-special-btn" onClick={addSpecial} className="cy-btn cy-btn-cyan text-[10px] min-h-0 px-2 py-1">
                    <Plus size={10} className="mr-1" /> ADD
                  </button>
                </div>
                <div className="space-y-1">
                  {form.specials.map((s, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto] gap-1 items-center" data-testid={`foe-special-row-${i}`}>
                      <input className="cy-input" placeholder="e.g. Regenerates 1 HP per round." value={s} onChange={(e) => editSpecial(i, e.target.value)} />
                      <button
                        onClick={() => removeSpecial(i)}
                        className="p-1 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
                        aria-label="Remove special"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-t-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                {user ? "Saved to your cloud roster" : "Saved locally on this device"}
              </div>
              <button
                data-testid="foe-save-btn"
                onClick={save}
                disabled={!valid || saving}
                className="cy-btn cy-btn-solid-yellow text-sm disabled:opacity-50"
              >
                <Save size={14} className="mr-1" /> {saving ? "SAVING…" : "SAVE FOE"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
